import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { RelayStatus } from "../protocol/event-types";
import type { StreamEvent } from "../protocol/frames";
import {
  createRelayConnection,
  type RelayConnection,
} from "./relay-connection";

type Registration = {
  channels: Set<string>;
  /** Keeps the stream open on a hidden page while this registration lasts. */
  liveWhileHidden: boolean;
  onEvent: (event: StreamEvent) => void;
};
export type RelayContextValue = {
  status: RelayStatus;
  register: (id: string, registration: Registration) => void;
  unregister: (id: string) => void;
};
export const RelayContext = createContext<RelayContextValue | null>(null);

const REGISTER_DEBOUNCE_MS = 25;

/** Every registered channel, and whether any registration needs the stream on a hidden page. */
function applyRegistrations(
  connection: RelayConnection,
  registrations: Iterable<Registration>,
) {
  const all = [...registrations];
  connection.setChannels(
    all.flatMap((r) => [...r.channels]),
    { liveWhileHidden: all.some((r) => r.liveWhileHidden) },
  );
}

export function RelayProvider(props: {
  url: string;
  getToken: () => Promise<string | null>;
  /** Changing it (e.g. the signed-in user id) reopens the connection with a fresh token. */
  identityKey?: string | null;
  /** Channels the relay serves without a token; a signed-out viewer connects to these alone. */
  isPublicChannel?: (channel: string) => boolean;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<RelayStatus>("disconnected");
  const getTokenRef = useRef(props.getToken);
  getTokenRef.current = props.getToken;
  const isPublicChannelRef = useRef(props.isPublicChannel);
  isPublicChannelRef.current = props.isPublicChannel;
  const registrations = useRef(new Map<string, Registration>());
  const connection = useRef<ReturnType<typeof createRelayConnection> | null>(
    null,
  );
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // identityKey is a deliberate trigger: a new user reopens the stream with their token.
  useEffect(() => {
    const conn = createRelayConnection({
      url: props.url,
      getToken: () => getTokenRef.current(),
      isPublicChannel: (channel) =>
        isPublicChannelRef.current?.(channel) ?? false,
      onStatus: setStatus,
      onEvent: (event) => {
        for (const registration of registrations.current.values()) {
          if (!registration.channels.has(event.channel)) continue;
          try {
            registration.onEvent(event);
          } catch (error) {
            // A throw here would abort the shared stream's read loop and starve the other
            // subscribers; rethrow off the loop so the error reporter still sees it.
            setTimeout(() => {
              throw error;
            });
          }
        }
      },
    });
    connection.current = conn;
    // A background tab lets its stream go after a while (relay-connection.ts) and reopens it when shown.
    const syncPageHidden = () =>
      conn.setPageHidden(document.visibilityState === "hidden");
    syncPageHidden();
    document.addEventListener("visibilitychange", syncPageHidden);
    applyRegistrations(conn, registrations.current.values());
    return () => {
      document.removeEventListener("visibilitychange", syncPageHidden);
      clearTimeout(debounce.current);
      conn.close();
      connection.current = null;
    };
  }, [props.url, props.identityKey]);

  const sync = useCallback(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      if (connection.current)
        applyRegistrations(connection.current, registrations.current.values());
    }, REGISTER_DEBOUNCE_MS);
  }, []);

  // Stable identities, so hooks don't re-register on every status change.
  const register = useCallback<RelayContextValue["register"]>(
    (id, registration) => {
      registrations.current.set(id, registration);
      sync();
    },
    [sync],
  );
  const unregister = useCallback<RelayContextValue["unregister"]>(
    (id) => {
      registrations.current.delete(id);
      sync();
    },
    [sync],
  );

  const value = useMemo<RelayContextValue>(
    () => ({ status, register, unregister }),
    [status, register, unregister],
  );

  return (
    <RelayContext.Provider value={value}>
      {props.children}
    </RelayContext.Provider>
  );
}
